// Operator Slack-lite Messages modal — London Smoke & Cure

import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { OVERLAY_IDS } from '../../context/OverlayManagerContext';
import { db } from '../../services/firebase';
import {
    chatDisplayName,
    consumePendingOperatorChatFocus,
    createOperatorChannel,
    DEFAULT_MESSAGE_PAGE,
    ensureOpsAllChannel,
    fetchPinnedMessages,
    listEligibleRecipients,
    loadOlderChatMessages,
    markChatRead,
    openOrCreateDm,
    pinChatMessage,
    sendChatMessage,
    subscribeChatMessages,
    subscribeChatStates,
    subscribeOperatorChats,
    unpinChatMessage,
    userHasAdminOrProductionRole,
} from '../../services/operatorChatService';
import {
    readOperatorMessagesChrome,
    writeOperatorMessagesChrome,
    readOperatorMessagesPinnedPosition,
    writeOperatorMessagesPinnedPosition,
} from '../../utils/operatorMessagesChrome';
import { useViewport } from '../../hooks/useViewport';
import { useOperatorWorkspaceShell } from '../operatorWorkspace/useOperatorWorkspaceShell';
import OperatorWorkspaceChromeHeader from '../operatorWorkspace/OperatorWorkspaceChromeHeader';
import OperatorChatSidebar from './OperatorChatSidebar';
import OperatorChatThread from './OperatorChatThread';
import OperatorChatComposer from './OperatorChatComposer';
import OperatorChatPinsBar from './OperatorChatPinsBar';
import './OperatorMessages.css';

export default function OperatorMessagesModal() {
    const { user } = useAuth();
    const { isMobile } = useViewport();
    const {
        panelOpen,
        expanded,
        pinned,
        modalRef,
        close,
        toggleExpand,
        togglePin,
        bringToFront,
        zIndex,
        hasCustomPosition,
        shellMoving,
        onHandlePointerDown,
        closeOverlay,
    } = useOperatorWorkspaceShell({
        overlayId: OVERLAY_IDS.OPERATOR_MESSAGES,
        readChrome: readOperatorMessagesChrome,
        writeChrome: writeOperatorMessagesChrome,
        readPinnedPosition: readOperatorMessagesPinnedPosition,
        writePinnedPosition: writeOperatorMessagesPinnedPosition,
    });

    const [fromUserData, setFromUserData] = useState(null);
    const [staffEligible, setStaffEligible] = useState(false);
    const [chats, setChats] = useState([]);
    const [chatStates, setChatStates] = useState({});
    const [chatsLoading, setChatsLoading] = useState(true);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [filterQuery, setFilterQuery] = useState('');
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [olderMessages, setOlderMessages] = useState([]);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasMoreOlder, setHasMoreOlder] = useState(true);
    const [pins, setPins] = useState([]);
    const [highlightMessageId, setHighlightMessageId] = useState(null);
    const [sending, setSending] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [error, setError] = useState('');
    const [pickerMode, setPickerMode] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [recipientQuery, setRecipientQuery] = useState('');
    const [channelName, setChannelName] = useState('');
    const [channelMemberIds, setChannelMemberIds] = useState([]);
    const [mobileShowThread, setMobileShowThread] = useState(false);

    const open = panelOpen;

    const selectedChat = useMemo(
        () => chats.find((c) => c.id === selectedChatId) || null,
        [chats, selectedChatId]
    );

    const allMessages = useMemo(() => {
        const byId = new Map();
        [...olderMessages, ...messages].forEach((m) => {
            if (m?.id) byId.set(m.id, m);
        });
        return Array.from(byId.values()).sort((a, b) => {
            const ta = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
            const tb = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
            return ta - tb;
        });
    }, [olderMessages, messages]);

    useEffect(() => {
        if (!open || !user?.uid) {
            setFromUserData(null);
            setStaffEligible(false);
            return undefined;
        }
        let cancelled = false;
        getDoc(doc(db, 'users', user.uid)).then((snap) => {
            if (cancelled) return;
            const data = snap.exists() ? { uid: user.uid, ...snap.data() } : { uid: user.uid };
            const allowed = userHasAdminOrProductionRole(data);
            setFromUserData(data);
            setStaffEligible(allowed);
            if (!allowed) closeOverlay(OVERLAY_IDS.OPERATOR_MESSAGES);
        });
        return () => {
            cancelled = true;
        };
    }, [open, user?.uid, closeOverlay]);

    useEffect(() => {
        if (!open || !staffEligible || !user?.uid) return undefined;
        let cancelled = false;
        setChatsLoading(true);
        ensureOpsAllChannel(user.uid)
            .catch((err) => console.warn('[operatorChat] ensure ops-all:', err))
            .finally(() => {
                if (!cancelled) setChatsLoading(false);
            });

        const unsubChats = subscribeOperatorChats(user.uid, (list) => {
            setChats(list);
            setChatsLoading(false);
        });
        const unsubStates = subscribeChatStates(user.uid, setChatStates);

        const focusId = consumePendingOperatorChatFocus();
        if (focusId) {
            setSelectedChatId(focusId);
            setMobileShowThread(true);
        }

        return () => {
            cancelled = true;
            unsubChats();
            unsubStates();
        };
    }, [open, staffEligible, user?.uid]);

    useEffect(() => {
        if (!open) {
            setSelectedChatId(null);
            setFilterQuery('');
            setMessages([]);
            setOlderMessages([]);
            setPins([]);
            setError('');
            setPickerMode(null);
            setSending(false);
            setUploadProgress(null);
            setMobileShowThread(false);
            setHighlightMessageId(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !selectedChatId) {
            setMessages([]);
            setOlderMessages([]);
            setHasMoreOlder(true);
            return undefined;
        }
        setMessagesLoading(true);
        setOlderMessages([]);
        setHasMoreOlder(true);
        const unsub = subscribeChatMessages(selectedChatId, ({ messages: live, loading }) => {
            setMessages(live);
            setMessagesLoading(loading);
            if (live.length < DEFAULT_MESSAGE_PAGE) setHasMoreOlder(false);
        });
        if (user?.uid) {
            markChatRead(user.uid, selectedChatId).catch(() => {});
        }
        return () => unsub();
    }, [open, selectedChatId, user?.uid]);

    useEffect(() => {
        if (!selectedChat?.pinnedMessageIds?.length) {
            setPins([]);
            return undefined;
        }
        let cancelled = false;
        fetchPinnedMessages(selectedChat.id, selectedChat.pinnedMessageIds).then((list) => {
            if (!cancelled) setPins(list);
        });
        return () => {
            cancelled = true;
        };
    }, [selectedChat?.id, selectedChat?.pinnedMessageIds]);

    useEffect(() => {
        if (!pickerMode || !user?.uid) return;
        listEligibleRecipients(user.uid)
            .then(setRecipients)
            .catch((err) => setError(err?.message || 'Could not load operators.'));
    }, [pickerMode, user?.uid]);

    const filteredRecipients = useMemo(() => {
        const q = recipientQuery.trim().toLowerCase();
        if (!q) return recipients;
        return recipients.filter((r) => `${r.name} ${r.email} ${r.role}`.toLowerCase().includes(q));
    }, [recipients, recipientQuery]);

    const selectChat = (chatId) => {
        setSelectedChatId(chatId);
        setError('');
        setPickerMode(null);
        setMobileShowThread(true);
        setHighlightMessageId(null);
    };

    const handleLoadOlder = async () => {
        if (!selectedChatId || loadingOlder || allMessages.length === 0) return;
        setLoadingOlder(true);
        try {
            const oldest = allMessages[0]?.createdAt;
            const older = await loadOlderChatMessages(selectedChatId, oldest);
            if (older.length === 0) {
                setHasMoreOlder(false);
            } else {
                setOlderMessages((prev) => [...older, ...prev]);
                if (older.length < DEFAULT_MESSAGE_PAGE) setHasMoreOlder(false);
            }
        } catch (err) {
            setError(err?.message || 'Could not load older messages.');
        } finally {
            setLoadingOlder(false);
        }
    };

    const handleSend = async ({ bodyHtml, files, driveItems, showAfterPresetId }) => {
        if (!user?.uid || !selectedChatId || sending) return;
        setSending(true);
        setError('');
        setUploadProgress(null);
        try {
            await sendChatMessage({
                chatId: selectedChatId,
                fromUid: user.uid,
                fromUserData,
                bodyHtml,
                files,
                driveItems,
                showAfterPresetId,
                onUploadProgress: setUploadProgress,
            });
        } finally {
            setSending(false);
            setUploadProgress(null);
        }
    };

    const handlePin = async (messageId) => {
        if (!user?.uid || !selectedChatId) return;
        try {
            await pinChatMessage({ chatId: selectedChatId, messageId, uid: user.uid });
        } catch (err) {
            setError(err?.message || 'Could not pin message.');
        }
    };

    const handleUnpin = async (messageId) => {
        if (!user?.uid || !selectedChatId) return;
        try {
            await unpinChatMessage({ chatId: selectedChatId, messageId, uid: user.uid });
        } catch (err) {
            setError(err?.message || 'Could not unpin message.');
        }
    };

    const handlePickDm = async (recipient) => {
        if (!user?.uid || !recipient) return;
        setError('');
        try {
            const chat = await openOrCreateDm({
                fromUid: user.uid,
                otherUid: recipient.uid,
                fromUserData,
                otherName: recipient.name,
            });
            setPickerMode(null);
            selectChat(chat.id);
        } catch (err) {
            setError(err?.message || 'Could not open DM.');
        }
    };

    const toggleChannelMember = (uid) => {
        setChannelMemberIds((prev) =>
            prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
        );
    };

    const handleCreateChannel = async () => {
        if (!user?.uid) return;
        setError('');
        try {
            const chat = await createOperatorChannel({
                createdByUid: user.uid,
                fromUserData,
                name: channelName,
                memberUids: channelMemberIds,
            });
            setChannelName('');
            setChannelMemberIds([]);
            setPickerMode(null);
            selectChat(chat.id);
        } catch (err) {
            setError(err?.message || 'Could not create channel.');
        }
    };

    if (!open || !staffEligible) return null;

    const headerTitle = selectedChat
        ? chatDisplayName(selectedChat, user?.uid)
        : 'Messages';

    return (
        <div
            className={`op-msg-overlay${expanded ? ' is-expanded' : ''}${
                pinned ? ' is-pinned' : ''
            }${shellMoving ? ' is-shell-moving' : ''}${isMobile ? ' is-mobile' : ''}`}
            role="presentation"
            style={zIndex ? { zIndex } : undefined}
            onClick={(e) => {
                if (e.target === e.currentTarget && !pinned) close();
            }}
        >
            <div
                ref={modalRef}
                className={`op-msg-modal op-msg-modal--workspace lsc-glass-chrome${
                    expanded ? ' is-expanded' : ''
                }${pinned ? ' is-pinned' : ''}${
                    hasCustomPosition ? ' is-repositioned' : ''
                }${shellMoving ? ' is-shell-moving' : ''}${isMobile ? ' is-mobile-sheet' : ''}`}
                role="dialog"
                aria-modal={pinned ? 'false' : 'true'}
                aria-label="Operator messages"
                onPointerDown={bringToFront}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="op-msg-modal__accent" aria-hidden="true" />
                <OperatorWorkspaceChromeHeader
                    title={headerTitle}
                    pinned={pinned}
                    expanded={expanded}
                    onToggleExpand={toggleExpand}
                    onTogglePin={togglePin}
                    onClose={close}
                    onHandlePointerDown={onHandlePointerDown}
                    closeAriaLabel="Close messages"
                    expandTitle="Expand — focus chats"
                    isMobile={isMobile}
                    extraActions={
                        isMobile && mobileShowThread && selectedChat ? (
                            <button
                                type="button"
                                className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact op-msg-back-mobile"
                                onClick={() => setMobileShowThread(false)}
                            >
                                Chats
                            </button>
                        ) : null
                    }
                />

                <div
                    className={`op-msg-workspace${mobileShowThread ? ' show-thread' : ''}${
                        isMobile ? ' is-mobile' : ''
                    }`}
                >
                    <OperatorChatSidebar
                        chats={chats}
                        chatStates={chatStates}
                        currentUid={user?.uid}
                        selectedChatId={selectedChatId}
                        filterQuery={filterQuery}
                        onFilterQueryChange={setFilterQuery}
                        onSelectChat={selectChat}
                        onStartDm={() => {
                            setPickerMode('dm');
                            setRecipientQuery('');
                        }}
                        onNewChannel={() => {
                            setPickerMode('channel');
                            setChannelName('');
                            setChannelMemberIds([]);
                            setRecipientQuery('');
                        }}
                        loading={chatsLoading}
                    />

                    <section className="op-msg-main" aria-label="Conversation">
                        {pickerMode === 'dm' ? (
                            <div className="op-msg-picker">
                                <h3 className="op-msg-picker__title">New direct message</h3>
                                <input
                                    className="op-msg-search"
                                    type="search"
                                    placeholder="Search Admin or Production…"
                                    value={recipientQuery}
                                    onChange={(e) => setRecipientQuery(e.target.value)}
                                />
                                <ul className="op-msg-recipient-list">
                                    {filteredRecipients.map((r) => (
                                        <li key={r.uid}>
                                            <button
                                                type="button"
                                                className={`op-msg-recipient${r.isSelf ? ' is-self' : ''}`}
                                                onClick={() => handlePickDm(r)}
                                            >
                                                <span className="op-msg-recipient__name">{r.name}</span>
                                                <span className="op-msg-recipient__meta">
                                                    {r.isSelf
                                                        ? 'Leave a reminder for yourself'
                                                        : [r.email, r.role].filter(Boolean).join(' · ')}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    type="button"
                                    className="op-msg-btn op-msg-btn--ghost"
                                    onClick={() => setPickerMode(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : null}

                        {pickerMode === 'channel' ? (
                            <div className="op-msg-picker">
                                <h3 className="op-msg-picker__title">New channel</h3>
                                <label className="op-msg-field-label" htmlFor="op-msg-channel-name">
                                    Channel name
                                </label>
                                <input
                                    id="op-msg-channel-name"
                                    className="op-msg-search"
                                    type="text"
                                    value={channelName}
                                    onChange={(e) => setChannelName(e.target.value)}
                                    placeholder="e.g. Pack floor"
                                />
                                <span className="op-msg-field-label">Members</span>
                                <input
                                    className="op-msg-search"
                                    type="search"
                                    placeholder="Filter operators…"
                                    value={recipientQuery}
                                    onChange={(e) => setRecipientQuery(e.target.value)}
                                />
                                <ul className="op-msg-recipient-list">
                                    {filteredRecipients
                                        .filter((r) => !r.isSelf)
                                        .map((r) => (
                                            <li key={r.uid}>
                                                <button
                                                    type="button"
                                                    className={`op-msg-recipient${
                                                        channelMemberIds.includes(r.uid) ? ' is-selected' : ''
                                                    }`}
                                                    onClick={() => toggleChannelMember(r.uid)}
                                                >
                                                    <span className="op-msg-recipient__name">{r.name}</span>
                                                    <span className="op-msg-recipient__meta">
                                                        {[r.email, r.role].filter(Boolean).join(' · ')}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                </ul>
                                <div className="op-msg-actions">
                                    <button
                                        type="button"
                                        className="op-msg-btn op-msg-btn--ghost"
                                        onClick={() => setPickerMode(null)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="op-msg-btn op-msg-btn--primary"
                                        onClick={handleCreateChannel}
                                    >
                                        Create channel
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        {!pickerMode && !selectedChat ? (
                            <div className="op-msg-empty op-msg-main-empty">
                                Select a channel or direct message, or start a new one.
                            </div>
                        ) : null}

                        {!pickerMode && selectedChat ? (
                            <>
                                <OperatorChatPinsBar
                                    pins={pins}
                                    onJump={(id) => setHighlightMessageId(id)}
                                    onUnpin={handleUnpin}
                                />
                                <OperatorChatThread
                                    messages={allMessages}
                                    loading={messagesLoading}
                                    currentUid={user?.uid}
                                    onLoadOlder={handleLoadOlder}
                                    loadingOlder={loadingOlder}
                                    hasMoreOlder={hasMoreOlder}
                                    onPin={handlePin}
                                    onUnpin={handleUnpin}
                                    highlightMessageId={highlightMessageId}
                                />
                                {error && !sending ? <p className="op-msg-error op-msg-error--pad">{error}</p> : null}
                                <OperatorChatComposer
                                    uid={user?.uid}
                                    disabled={false}
                                    sending={sending}
                                    uploadProgress={uploadProgress}
                                    error={error}
                                    onClearError={() => setError('')}
                                    onSend={handleSend}
                                />
                            </>
                        ) : null}
                    </section>
                </div>
            </div>
        </div>
    );
}
